/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useQuery } from '@tanstack/react-query';

import { meQuery } from '@/api/auth';
import { Icon } from '@/components/ui/Icon';
import type { IconName } from '@/components/ui/icons';
import { ApiError, ContractError } from '@/lib/queryClient';

/**
 * The three states that are easy to skip.
 *
 * Every screen implements all four: loading, empty, error, success (`UX-3`,
 * `SCR-1`). Success is the screen itself; these are the rest.
 */

/** Loading: a skeleton, so the layout does not jump when the answer arrives. */
export function Rows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="sk">
      {Array.from({ length: rows }, (_, i) => (
        <div className="sk-row" key={i}>
          <span className="sk-bar" style={{ width: '14%' }} />
          <span className="sk-bar" style={{ width: '46%' }} />
          <span className="sk-bar" style={{ width: '18%' }} />
        </div>
      ))}
    </div>
  );
}

/** Empty: says which kind of empty it is, and offers the way out. */
export function Empty({
  icon = 'doc',
  title,
  detail,
  action,
}: {
  icon?: IconName;
  title: string;
  detail: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty-i">
        <Icon name={icon} size={22} />
      </span>
      <b>{title}</b>
      <p>{detail}</p>
      {action ? <div className="empty-a">{action}</div> : null}
    </div>
  );
}

/** What a refusal says to somebody who holds a role, when the page does not say. */
const DENIED =
  'This page needs a permission your role does not include. Ask a Vellum administrator if you need it.';

/** What a refusal says to somebody with no role at all. */
const NO_ROLE =
  'Your account has no role yet, so there is nothing here to show. ' +
  'An administrator grants a role through the group map.';

/**
 * Error: recoverable, and never the raw error text.
 *
 * The correlation id is offered instead, because that is the thing a person can
 * usefully quote and the only part of the failure that is safe to show.
 */
export function Failed({
  title,
  detail,
  denied,
  error,
  onRetry,
}: {
  title?: string;
  detail?: string;
  /**
   * What a refusal (403) says to somebody who holds a role but not the one
   * this needs: "Only Admin, Contracts and Leadership can see People." Somebody with no role at
   * all is told that instead, whatever the page says.
   */
  denied?: string;
  error?: unknown;
  onRetry?: () => void;
}) {
  const api = error instanceof ApiError ? error : undefined;
  // The server answered; we could not read the answer. Almost always a running
  // API older than the page in front of it.
  const stale = error instanceof ContractError;
  const reference = api?.correlationId ?? '';

  // A refusal is not an outage. Telling somebody to check the API is running
  // when the server understood them perfectly and said no sends them to debug
  // the wrong thing -- and a retry cannot help, so it is not offered.
  const forbidden = api?.status === 403;
  // Nor is a session that ended. The person is on their way to the sign-in
  // screen (`createQueryClient`); for the moment before they arrive, the page
  // says why rather than sending them to look at the API.
  const ended = api?.status === 401;

  const heading =
    title ??
    (forbidden
      ? 'Not available to you'
      : ended
        ? 'Your session has ended'
        : stale
          ? 'The API answered with something this page cannot read'
          : 'That could not be loaded');
  const body =
    detail ??
    (forbidden
      ? null
      : ended
        ? 'Sign in again to carry on.'
        : stale
          ? 'It is running, but on older code than this page — restart it, ' +
            'then try again.'
          : 'Check that the API is running, then try again.');

  return (
    <div className="empty">
      <span className={`empty-i ${forbidden ? '' : 'late'}`}>
        <Icon name={forbidden ? 'shield' : 'warn'} size={22} />
      </span>
      <b>{heading}</b>
      {body === null ? <Refused denied={denied} /> : <p>{body}</p>}
      {onRetry && !forbidden ? (
        <div className="empty-a">
          <button type="button" className="btn btn-p" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
      {reference ? <p className="ref">Reference {reference}</p> : null}
    </div>
  );
}

/**
 * Why a refusal: a role that does not reach this, or no role at all.
 *
 * Every refusal is the same `forbidden` problem, so whether somebody has no
 * role is a fact about them, not about the refusal: read from who is signed
 * in, which every signed-in page has loaded already. Until it is known they
 * are told what their role does not reach, never that they have none.
 */
function Refused({ denied }: { denied: string | undefined }) {
  const { data: me } = useQuery(meQuery());
  return <p>{me && me.roles.length === 0 ? NO_ROLE : (denied ?? DENIED)}</p>;
}
